import {
  useCallback,
  useRef,
  type RefObject,
} from 'react';
import { useCmarkdowntablePopoverDragHandlers } from './useCmarkdowntablePopoverDragHandlers';
import { useCmarkdowntablePopoverResizeHandlers } from './useCmarkdowntablePopoverResizeHandlers';
import type {
  CmarkdowntablePopoverRect,
  PopoverDragState,
  PopoverResizeState,
  TablePopoverPointerHandler,
} from './Cmarkdowntable.types';

interface UseCmarkdowntablePopoverPointerHandlersInput {
  popoverRef: RefObject<HTMLDivElement | null>;
  applyManualRect: (rect: CmarkdowntablePopoverRect | null) => void;
}

interface CmarkdowntablePopoverPointerHandlers {
  resetPointerState: () => void;
  handleHeaderPointerDown: TablePopoverPointerHandler;
  handleHeaderPointerMove: TablePopoverPointerHandler;
  handleHeaderPointerEnd: TablePopoverPointerHandler;
  handleResizePointerDown: TablePopoverPointerHandler;
  handleResizePointerMove: TablePopoverPointerHandler;
  handleResizePointerEnd: TablePopoverPointerHandler;
}

export function useCmarkdowntablePopoverPointerHandlers(
  input: UseCmarkdowntablePopoverPointerHandlersInput,
): CmarkdowntablePopoverPointerHandlers {
  const { popoverRef, applyManualRect } = input;
  const dragStateRef = useRef<PopoverDragState | null>(null);
  const resizeStateRef = useRef<PopoverResizeState | null>(null);

  const resetPointerState = useCallback((): void => {
    dragStateRef.current = null;
    resizeStateRef.current = null;
  }, []);

  const {
    handleHeaderPointerDown,
    handleHeaderPointerMove,
    handleHeaderPointerEnd,
  } = useCmarkdowntablePopoverDragHandlers({
    dragStateRef,
    popoverRef,
    applyManualRect,
  });
  const {
    handleResizePointerDown,
    handleResizePointerMove,
    handleResizePointerEnd,
  } = useCmarkdowntablePopoverResizeHandlers({
    resizeStateRef,
    popoverRef,
    applyManualRect,
  });

  return {
    resetPointerState,
    handleHeaderPointerDown,
    handleHeaderPointerMove,
    handleHeaderPointerEnd,
    handleResizePointerDown,
    handleResizePointerMove,
    handleResizePointerEnd,
  };
}
